import React, { useState } from "react";
import { AiOutlineClose, AiOutlineCheckCircle, AiOutlineWarning, AiOutlineCloseCircle, AiOutlineSync } from "react-icons/ai";

/**
 * Muestra el progreso de un batch async (approve/review/reject) con barra,
 * contadores, botón de cancelar y detalle colapsable de errores.
 *
 * Props:
 *  - task: BatchTaskStatus (del API) — {taskId, operation, fase, running, porcentaje,
 *          procesados, totalDocs, exitosos, fallidos, failedDocs[]}
 *  - onCancel(): llamado al click en "Cancelar" (mientras task.running)
 *  - onClose(): llamado al click en "Cerrar" (sólo cuando !task.running)
 *
 * Ver FSD-010 REQ-1011/1012/1013 y estilo coherente con AdminIndexPage.
 */
const OPERATION_LABELS = {
    SIGNATURE: "Firma masiva",
    APPROVE: "Aprobación masiva",
    REVIEW: "Revisión masiva",
    REJECT_REVIEW: "Rechazo masivo (revisión)",
    REJECT_APPROVAL: "Rechazo masivo (aprobación)",
};

const PHASE_LABELS = {
    INICIANDO: "Iniciando...",
    PROCESANDO: "Procesando...",
    COMPLETADO: "Completado",
    CANCELADO: "Cancelado",
    ERROR: "Error",
};

const BatchProgressBar = ({ task, onCancel, onClose }) => {
    const [showErrors, setShowErrors] = useState(false);

    if (!task) return null;

    const operationLabel = OPERATION_LABELS[task.operation] || task.operation || "Operación masiva";
    const phaseLabel = PHASE_LABELS[task.fase] || task.fase || "-";
    const pct = Math.max(0, Math.min(100, task.porcentaje || 0));

    const isRunning = task.running;
    const isCompleted = task.fase === "COMPLETADO";
    const isCancelled = task.fase === "CANCELADO";
    const isError = task.fase === "ERROR";
    const hasFailures = (task.fallidos || 0) > 0 && (task.failedDocs || []).length > 0;

    const iconByPhase = () => {
        if (isRunning) return <AiOutlineSync style={{ ...styles.icon, animation: "spin 2s linear infinite" }} />;
        if (isCompleted && (task.fallidos || 0) === 0) return <AiOutlineCheckCircle style={{ ...styles.icon, color: "#10B981" }} />;
        if (isCompleted && (task.fallidos || 0) > 0) return <AiOutlineWarning style={{ ...styles.icon, color: "#F59E0B" }} />;
        if (isCancelled) return <AiOutlineCloseCircle style={{ ...styles.icon, color: "#6B7280" }} />;
        if (isError) return <AiOutlineCloseCircle style={{ ...styles.icon, color: "#EF4444" }} />;
        return null;
    };

    const barColor = () => {
        if (isError) return "#EF4444";
        if (isCancelled) return "#6B7280";
        if (isCompleted && (task.fallidos || 0) > 0) return "#F59E0B";
        if (isCompleted) return "#10B981";
        return "#00468B";
    };

    return (
        <div style={styles.container} data-testid="batch-progress-bar">
            <div style={styles.header}>
                {iconByPhase()}
                <div style={styles.title}>
                    <div style={styles.operation}>{operationLabel}</div>
                    <div style={styles.phase}>{phaseLabel}</div>
                </div>
                <div style={styles.counters}>
                    <span data-testid="batch-procesados">{task.procesados || 0}</span>
                    {" / "}
                    <span data-testid="batch-total">{task.totalDocs || 0}</span>
                    {" · "}
                    <span data-testid="batch-porcentaje">{pct}%</span>
                </div>
                <div style={styles.actions}>
                    {isRunning && (
                        <button
                            onClick={onCancel}
                            style={styles.cancelButton}
                            data-testid="batch-cancel"
                            type="button"
                        >
                            Cancelar
                        </button>
                    )}
                    {!isRunning && (
                        <button
                            onClick={onClose}
                            style={styles.closeButton}
                            data-testid="batch-close"
                            type="button"
                            aria-label="Cerrar"
                        >
                            <AiOutlineClose />
                        </button>
                    )}
                </div>
            </div>

            <div style={styles.barOuter}>
                <div
                    style={{
                        ...styles.barInner,
                        width: `${pct}%`,
                        backgroundColor: barColor(),
                    }}
                    data-testid="batch-bar-inner"
                />
            </div>

            <div style={styles.stats}>
                <span>
                    <strong data-testid="batch-exitosos">{task.exitosos || 0}</strong> exitosos
                </span>
                {(task.fallidos || 0) > 0 && (
                    <span style={{ color: "#EF4444" }}>
                        {" · "}
                        <strong data-testid="batch-fallidos">{task.fallidos}</strong> fallidos
                    </span>
                )}
                {task.mensaje && (
                    <span style={styles.mensaje}>{" · " + task.mensaje}</span>
                )}
            </div>

            {hasFailures && (
                <div style={styles.errorsBlock}>
                    <button
                        style={styles.toggleErrors}
                        onClick={() => setShowErrors(v => !v)}
                        type="button"
                        data-testid="batch-toggle-errors"
                    >
                        {showErrors ? "Ocultar" : "Ver"} detalle de errores ({task.failedDocs.length})
                    </button>
                    {showErrors && (
                        <ul style={styles.errorsList} data-testid="batch-errors-list">
                            {task.failedDocs.map(fd => (
                                <li key={fd.idDoc}>
                                    <code>#{fd.idDoc}</code>: {fd.error}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        border: "1px solid #E5E7EB",
        borderRadius: "6px",
        padding: "10px 14px",
        backgroundColor: "#F9FAFB",
        marginBottom: "10px",
    },
    header: { display: "flex", alignItems: "center", gap: "10px" },
    icon: { fontSize: "20px", color: "#00468B", flexShrink: 0 },
    title: { flex: 1, minWidth: 0 },
    operation: { fontWeight: "bold", color: "#111827", fontSize: "14px" },
    phase: { color: "#6B7280", fontSize: "12px" },
    counters: { color: "#111827", fontSize: "13px", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    actions: { flexShrink: 0 },
    cancelButton: {
        backgroundColor: "#fff",
        color: "#EF4444",
        border: "1px solid #EF4444",
        borderRadius: "4px",
        padding: "4px 10px",
        cursor: "pointer",
        fontSize: "12px",
        fontWeight: "bold",
    },
    closeButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        color: "#6B7280",
        fontSize: "18px",
        display: "flex",
        alignItems: "center",
    },
    barOuter: {
        height: "8px",
        backgroundColor: "#E5E7EB",
        borderRadius: "4px",
        overflow: "hidden",
        marginTop: "8px",
    },
    barInner: {
        height: "100%",
        transition: "width 0.3s ease, background-color 0.3s ease",
    },
    stats: { marginTop: "6px", fontSize: "13px", color: "#111827" },
    mensaje: { color: "#6B7280", fontStyle: "italic" },
    errorsBlock: { marginTop: "8px" },
    toggleErrors: {
        background: "none",
        border: "none",
        color: "#00468B",
        cursor: "pointer",
        fontSize: "12px",
        padding: 0,
        textDecoration: "underline",
    },
    errorsList: {
        marginTop: "6px",
        paddingLeft: "20px",
        fontSize: "12px",
        color: "#111827",
        maxHeight: "150px",
        overflowY: "auto",
    },
};

export default BatchProgressBar;
